import React, { useRef } from "react";

import * as styles from "./atom.progress-bar.style";
import { useDragHandle } from "./hook.drag-handle";

type Props = {
  progress: number;

  isPlaying: boolean;
  durationMs: number;
  resume(): void;
  pause(): void;
  seek(progress: number): void;

  disabled: boolean;
};

export function Progressbar(props: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  const wasPlayingBeforeDragRef = useRef(props.isPlaying);

  const onDragBegin = () => {
    wasPlayingBeforeDragRef.current = props.isPlaying;
    props.pause();
  };

  const onDragEnded = (progress: number) => {
    props.seek(progress * props.durationMs);

    if (wasPlayingBeforeDragRef.current) {
      props.resume();
    }
  };

  const onKeyDown = (evt: React.KeyboardEvent) => {
    switch (evt.key) {
      case "Enter":
      case " ":
        if (props.isPlaying) {
          props.pause();
        } else {
          props.resume();
        }
        break;

      case "ArrowLeft":
        props.seek(
          Math.max(
            0,
            props.progress * props.durationMs - props.durationMs * 0.05,
          ),
        );
        break;

      case "ArrowRight":
        props.seek(
          Math.min(
            props.progress * props.durationMs + props.durationMs * 0.05,
            props.durationMs,
          ),
        );
        break;
    }
  };

  const [isDragging, dragHandlePosition] = useDragHandle(containerRef, {
    onDragBegin,
    onDragEnded,
  });

  const currentProgress = isDragging ? dragHandlePosition : props.progress;

  return (
    <div
      className={styles.wrapper}
      style={styles.tokens.filled.__override(currentProgress)}
      data-is-dragging={isDragging}
      data-disabled={props.disabled}
    >
      <div ref={containerRef} className={styles.track}>
        {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
        <button
          className={styles.dragHandle}
          style={styles.tokens.dragHandleOffset.__override(currentProgress)}
          onKeyDown={onKeyDown}
          aria-label="Søg i lydfil"
          aria-description="Søg i lydfil ved brug af venstre- og højre-piletasterne"
          disabled={props.disabled}
        >
          <span data-thumb />
          <span data-outline />
        </button>
      </div>
    </div>
  );
}
